import { traverseToFindImagesOnPage } from '@lib/get-component-props'
import { getOrigin } from '@lib/get-origin'
import { GTM_ID, pageview } from '@lib/gtm'
import useQueryState from '@lib/hooks/useQueryState/useQueryState'

import {
  selectAlpha2Code,
  selectCountries,
  selectRegions,
} from '@redux/globalSlice'

import MarketMatchingModal from '@components/ui/Modal/Country/MarketMatchingModal'
import { generateMediaUrlV2 } from '@lib/utils'

import { removeCartCookie } from '@features/cart/utils'
import { loadWishlist, setInitialWishlist } from '@redux/wishlistSlice'
import { mapEdgesToItems } from '@utils/map'
import parse from 'html-react-parser'
import Cookies from 'js-cookie'
import { NextSeo } from 'next-seo'
import NextHead from 'next/head'
import { useRouter } from 'next/router'
import Script from 'next/script'
import { pathToRegexp } from 'path-to-regexp'
import { Dispatch, FC, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { PageProps } from '../Layout/Layout'

type HeadProps = {}

const handleRouteChange = (
  url: string,
  refUrl: string,
  dispatch: Dispatch<any>,
) => {
  if (url.includes('/confirmation')) {
    removeCartCookie()
  } else {
  }

  const initialWishlist = loadWishlist() // Use your utility function
  dispatch(setInitialWishlist(initialWishlist))

  pageview(url, refUrl)

  // if (!sessionStorage.getItem('hideMarketMatchingModal'))
  //   dispatch(openModalView('MARKET_MATCHING_MODAL'))
}

const Head: FC<PageProps & HeadProps> = ({ pageProps }) => {
  const router = useRouter()
  const { country, language } = router.query
  const regions = useSelector(selectRegions)
  const countries = useSelector(selectCountries)
  const alpha2Code = useSelector(selectAlpha2Code)

  const dispatch = useDispatch()
  const regexp = pathToRegexp('/:lang/:country/(.*)?')
  const route = useMemo(() => {
    return regexp.exec(router.asPath.split('?')[0])
  }, [router.asPath])
  const product = pageProps?.productV2
  const [cartQuery, setCartQuery] = useQueryState('cart')
  const [sg_idQuery, setSg_idQuery] = useQueryState('sg_id')
  const [wishListQuery, setWishListQuery] = useQueryState('wishList')
  const exitMarketModal = useMemo(
    () =>
      router.asPath.includes('/cart') || router.asPath.includes('/checkout'),
    [router.asPath],
  )

  const [mmOpen, setMmOpen] = useState(false)
  useEffect(() => {
    if (exitMarketModal) {
      setMmOpen(false)
      return
    }

    fetch('/api/cookie-handler')
      .then((data) => data.json())
      .then((response) => {
        response.homeCountry &&
          Cookies.set('homeCountry', response.homeCountry?.toLowerCase(), {
            path: '/',
            expires: 30,
          })

        let homeCountry = Cookies.get('homeCountry')?.toLowerCase()
        const hideMarketMatchingModal =
          sessionStorage.getItem('hideMarketMatchingModal') !== null
        homeCountry =
          countries
            .find((c) => c.code?.toLowerCase() === homeCountry)
            ?.code?.toLowerCase() ?? 'int'

        if (
          Cookies.get('country') !== (country as string) &&
          country !== homeCountry &&
          !hideMarketMatchingModal
        ) {
          setMmOpen(true)
        }
        Cookies.set('country', country as string, {
          path: '/',
          expires: 30,
        })

        if (homeCountry && country !== homeCountry && !hideMarketMatchingModal)
          setMmOpen(true)
      })
  }, [alpha2Code, exitMarketModal])

  const currentUrlRef = useRef(getOrigin() + router.asPath)
  const initialRender = useRef(true)

  useEffect(() => {
    if (initialRender.current) {
      handleRouteChange(currentUrlRef.current, '', dispatch)
      initialRender.current = false
    }

    const handleRouteChangeComplete = (url: string) => {
      const newUrl = getOrigin() + url
      if (currentUrlRef.current !== newUrl) {
        handleRouteChange(newUrl, currentUrlRef.current, dispatch)
        currentUrlRef.current = newUrl
      }
    }

    router.events.on('routeChangeComplete', handleRouteChangeComplete)

    router.beforePopState((state) => {
      state.options.scroll = false
      return true
    })

    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete)
    }
  }, [router, regions, dispatch])

  useEffect(() => {
    if (sg_idQuery)
      Cookies.set('sg_id', sg_idQuery as string, {
        path: '/',
        expires: 730,
        domain: '.gubi.com',
      })
    if (wishListQuery) {
      Cookies.set('wishList', wishListQuery as string, {
        path: '/',
        expires: 60 * 60 * 24 * 30,
        domain: '.gubi.com',
      })
    }

    if (cartQuery) {
      Cookies.set('cart', cartQuery as string, {
        path: '/',
        expires: 60 * 60 * 24 * 30,
        domain: '.gubi.com',
      })
    }
  }, [cartQuery, sg_idQuery, wishListQuery])

  const indexUrl = `${getOrigin()}/${language}/${(country as string)
    ?.toLowerCase()
    .replace(/itemnumber/g, 'itemNumber')}`

  let canonicalUrl = (
    getOrigin() + (router.asPath === '/' ? '' : router.asPath)
  )?.split('?')[0]

  // if (pageProps.product && !!pageProps.product['heroVariant']) {
  //   // we need to include the itemNumber of the hero variant
  //   canonicalUrl += `?itemNumber=${pageProps.product['heroVariant'].itemNumber}`
  // }

  if (!pageProps.country) return <></>

  const productVariants = mapEdgesToItems(pageProps.productV2?.variants)
  return (
    <>
      <NextSeo
        {...{
          title: pageProps.page?.title,
          description: pageProps.page?.metaDescription,
          nofollow:
            process.env.NEXT_PUBLIC_ENVIRONMENT === 'production'
              ? pageProps.page?.nofollow
              : true,
          noindex:
            process.env.NEXT_PUBLIC_ENVIRONMENT === 'production'
              ? pageProps.page?.noindex
              : true,
          canonical: canonicalUrl,
          openGraph: {
            title: pageProps.page?.title,
            description: pageProps.page?.metaDescription,
            type: 'website',
            url: getOrigin() + router.asPath?.split('?')[0],
            locale: `${language}_${(country as string)?.toUpperCase()}`,
          },
        }}
      />
      <NextHead>
        <meta
          className="elastic"
          name="type"
          content={router.asPath.includes('/products/') ? 'product' : 'page'}
        />
        {product ? (
          <>
            <meta
              className="elastic"
              property="og:image"
              key="image"
              name="image"
              content={(productVariants?.length > 0 &&
              productVariants?.find((x) => x)
                ? generateMediaUrlV2(
                    productVariants
                      ?.find((x) => x.sku == product.heroVariant.sku)
                      .media.edges?.find(({ node }) => node.src)?.node.src,
                  )
                : generateMediaUrlV2(
                    productVariants?.find((x) => x.media.edges.length > 0)
                      ?.media[0].src,
                  )
              )?.replace('{size}', '1200')}
            />
            <meta
              className="elastic"
              name="document_title"
              content={product.name}
            ></meta>
          </>
        ) : (
          <>
            <meta
              className="elastic"
              name="document_title"
              content={pageProps.page?.title}
            ></meta>
            <meta
              className="elastic"
              property="og:image"
              key="image"
              name="image"
              content={`${
                traverseToFindImagesOnPage({
                  ...pageProps?.page?.components,
                })[0]?.url
              }?anchor=center&mode=crop&width=${1200}&height=0&upscale=false&format=jpg&quality=${75}`}
            />
          </>
        )}

        <meta
          name="viewport"
          content="width=device-width, height=device-height, initial-scale=1.0, user-scalable=0, minimum-scale=1.0, maximum-scale=1.0"
        />

        <link rel="preconnect" href="https://media.gubi.com" />
        <link rel="dns-prefetch" href="https://media.gubi.com" />

        {route &&
          regions?.map((region) =>
            region.countries
              .filter(
                (x) =>
                  x.code?.toLowerCase() != 'int' &&
                  x.code?.toLowerCase() != 'se' &&
                  x.code?.toLowerCase() != 'dk',
              )
              .map((c) => (
                <link
                  key={`${c.code}`}
                  rel="alternate"
                  hrefLang={`${'en'}-${c.code?.toUpperCase()}`}
                  href={`${getOrigin()}/${language}/${c.code?.toLowerCase()}${
                    route[3] ? `/${route[3]}` : ''
                  }`}
                ></link>
              )),
          )}
        <link
          rel="alternate"
          hrefLang="da-DK"
          href={`${getOrigin()}/en/dk${route[3] ? `/${route[3]}` : ''}`}
        ></link>
        <link
          rel="alternate"
          hrefLang="sv-SE"
          href={`${getOrigin()}/en/se${route[3] ? `/${route[3]}` : ''}`}
        ></link>
        <link
          rel="alternate"
          hrefLang="en"
          href={`${getOrigin()}/en/int${route[3] ? `/${route[3]}` : ''}`}
        />
        <link
          rel="alternate"
          hrefLang="x-default"
          href={`${getOrigin()}${route[3] ? `/${route[3]}` : ''}`}
        />
        <link
          rel="preconnect"
          href="https://policy.app.cookieinformation.com"
        />

        {pageProps.page?.script && parse(pageProps.page.script)}
        {/* CookieConsent */}
      </NextHead>

      {router.query.pages?.includes('home') && (
        <Script
          id="corporation-schema"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'http://schema.org',
              '@type': 'Corporation',
              '@id': 'https://gubi.com/#corporation',
              name: 'GUBI',
              legalName: 'GUBI A/S',
              description:
                'GUBI is a design house renowned for designing, developing and marketing a daring and elegant collection of furniture, lighting and interior objects that resonates across the globe. Its signature pieces evoke emotion and tell meaningful stories that make them both timeless and enduring. GUBI brings forgotten icons of the past together with tomorrow’s classics. The result is a visionary collection that spans almost 100 years of history and is sold all over the world.',
              logo: 'https://media.gubi.com/c2d193f8-d8a0-4209-aa22-a671507ab9ed.png',
              url: 'https://gubi.com',
              foundingDate: '1967',
              telephone: '+4533326368',
              sameAs: [
                'https://www.linkedin.com/company/gubi/',
                'https://www.facebook.com/gubiofficial/',
                'https://www.instagram.com/gubiofficial',
                'https://www.pinterest.dk/gubiofficial',
              ],
              address: [
                {
                  '@type': 'PostalAddress',
                  streetAddress: 'Klubiensvej 7-9',
                  addressLocality: 'Copenhagen',
                  postalCode: '2150',
                  addressCountry: 'DK',
                },
              ],
            }),
          }}
        />
      )}

      <Script
        id="search-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            '@id': `${indexUrl}#website`,
            url: `${indexUrl}`,
            potentialAction: {
              '@type': 'SearchAction',
              target: {
                '@type': 'EntryPoint',
                urlTemplate: `${indexUrl}?q={search_term_string}`,
              },
              'query-input': 'required name=search_term_string',
            },
          }),
        }}
      />
      <Script
        id="gtag-base"
        dangerouslySetInnerHTML={{
          __html: `
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s);j.async=true;j.src="https://load.tracking.gubi.com/eguamfxtg.js?"+i;
          f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','2ml=${encodeURIComponent(btoa('id=' + GTM_ID))}&page=1');
          `,
        }}
      />

      <MarketMatchingModal isOpen={mmOpen} setIsOpen={setMmOpen} />
    </>
  )
}

export default Head
